import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = []
  static values = {}

  connect() {
    console.log("Landing page form controller connected")

    // Initialize all sections as collapsed
    this.sections = new Map()

    // Count sections for debugging
    const sectionCount = document.querySelectorAll('[data-section]').length
    console.log(`Found ${sectionCount} collapsible sections`)

    // Collapse all sections immediately on load
    this.collapseAll()

    // Also collapse after a brief delay to handle any race conditions
    setTimeout(() => {
      console.log("Re-collapsing sections after delay")
      this.collapseAll()
    }, 50)

    // Setup image preview handlers
    this.setupImagePreviews()
  }

  toggleSection(event) {
    const button = event.currentTarget
    const sectionContainer = button.closest('[data-section]')
    const content = sectionContainer.querySelector('[data-section-content]')

    console.log('Toggle section clicked:', sectionContainer.dataset.section)
    console.log('Content element found:', !!content)

    // Get the last SVG (chevron) in the button
    const svgs = button.querySelectorAll('svg')
    const chevron = svgs[svgs.length - 1]

    if (!content) {
      console.error("No content found for section:", sectionContainer.dataset.section)
      console.error("Section HTML:", sectionContainer.innerHTML.substring(0, 200))
      return
    }

    const isExpanded = content.style.display !== 'none' && content.style.display !== ''
    console.log('Is expanded:', isExpanded, 'Display:', content.style.display)

    if (isExpanded) {
      // Collapse
      console.log('Collapsing section')
      content.style.display = 'none'
      if (chevron) {
        chevron.style.transform = 'rotate(0deg)'
      }
      this.sections.set(sectionContainer.dataset.section, false)
    } else {
      // Expand
      console.log('Expanding section')
      content.style.display = 'block'
      if (chevron) {
        chevron.style.transform = 'rotate(180deg)'
      }
      this.sections.set(sectionContainer.dataset.section, true)
    }
  }

  expandAll() {
    document.querySelectorAll('[data-section]').forEach(section => {
      const content = section.querySelector('[data-section-content]')
      const button = section.querySelector('button')

      if (content && button) {
        const svgs = button.querySelectorAll('svg')
        const chevron = svgs[svgs.length - 1]

        content.style.display = 'block'
        if (chevron) {
          chevron.style.transform = 'rotate(180deg)'
        }
      }
    })
  }

  collapseAll() {
    const sections = document.querySelectorAll('[data-section]')
    console.log(`Collapsing ${sections.length} sections`)

    sections.forEach((section, index) => {
      const content = section.querySelector('[data-section-content]')
      const button = section.querySelector('button')

      if (content && button) {
        const svgs = button.querySelectorAll('svg')
        const chevron = svgs[svgs.length - 1]

        content.style.display = 'none'
        if (chevron) {
          chevron.style.transform = 'rotate(0deg)'
        }

        const sectionName = section.dataset.section || 'unknown'
        console.log(`  - Collapsed section: ${sectionName}`)
      } else {
        console.warn(`  - Section ${index} missing content or button`)
      }
    })
  }

  applyColorPreset(event) {
    const preset = event.currentTarget.dataset.preset
    const presets = {
      default: {
        primary: '#1e3a5f',
        secondary: '#f5f7fb',
        accent: '#0C7A78'
      },
      ocean: {
        primary: '#0891b2',
        secondary: '#06b6d4',
        accent: '#3b82f6'
      },
      forest: {
        primary: '#166534',
        secondary: '#dcfce7',
        accent: '#15803d'
      },
      sunset: {
        primary: '#dc2626',
        secondary: '#f97316',
        accent: '#fbbf24'
      },
      purple: {
        primary: '#7c3aed',
        secondary: '#a78bfa',
        accent: '#c084fc'
      },
      monochrome: {
        primary: '#374151',
        secondary: '#6b7280',
        accent: '#9ca3af'
      }
    }

    const colors = presets[preset]
    if (colors) {
      const primaryColorInput = document.querySelector('[id$="_primary_color"]')
      const secondaryColorInput = document.querySelector('[id$="_secondary_color"]')
      const accentColorInput = document.querySelector('[id$="_accent_color"]')

      if (primaryColorInput) {
        primaryColorInput.value = colors.primary
        // Update both color picker and text input
        const colorPicker = primaryColorInput.previousElementSibling
        if (colorPicker && colorPicker.type === 'color') {
          colorPicker.value = colors.primary
        }
      }
      if (secondaryColorInput) {
        secondaryColorInput.value = colors.secondary
        const colorPicker = secondaryColorInput.previousElementSibling
        if (colorPicker && colorPicker.type === 'color') {
          colorPicker.value = colors.secondary
        }
      }
      if (accentColorInput) {
        accentColorInput.value = colors.accent
        const colorPicker = accentColorInput.previousElementSibling
        if (colorPicker && colorPicker.type === 'color') {
          colorPicker.value = colors.accent
        }
      }

      // Visual feedback
      event.currentTarget.classList.add('ring-2', 'ring-indigo-500', 'ring-offset-2')
      setTimeout(() => {
        event.currentTarget.classList.remove('ring-2', 'ring-indigo-500', 'ring-offset-2')
      }, 1000)
    }
  }

  setupImagePreviews() {
    // Setup preview for file inputs
    document.querySelectorAll('input[type="file"]').forEach(input => {
      if (input.accept.includes('image')) {
        input.addEventListener('change', (e) => this.previewImage(e))
      }
    })

    // Sync color picker with text input
    document.querySelectorAll('input[type="color"]').forEach(colorPicker => {
      const textInput = colorPicker.nextElementSibling
      if (textInput && textInput.type === 'text') {
        colorPicker.addEventListener('input', (e) => {
          textInput.value = e.target.value
        })
        textInput.addEventListener('input', (e) => {
          if (/^#[0-9A-F]{6}$/i.test(e.target.value)) {
            colorPicker.value = e.target.value
          }
        })
      }
    })
  }

  previewImage(event) {
    const input = event.target
    const file = input.files[0]

    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader()

      reader.onload = (e) => {
        // Find or create preview container
        let previewContainer = input.parentElement.querySelector('.image-preview')

        if (!previewContainer) {
          previewContainer = document.createElement('div')
          previewContainer.className = 'image-preview mt-2 relative inline-block'
          input.parentElement.insertBefore(previewContainer, input.nextSibling)
        }

        previewContainer.innerHTML = `
          <img src="${e.target.result}" class="h-32 w-auto rounded-lg border-2 border-indigo-500 shadow-lg" alt="Preview">
          <span class="absolute top-1 right-1 bg-indigo-500 text-white text-xs px-2 py-1 rounded font-medium">New Image</span>
        `
      }

      reader.readAsDataURL(file)
    }
  }
}
